/**
 * Simple Auth Context for Budget App
 * Works with the existing FedermanBenjamin Firebase database without requiring a 'users' collection
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from './firebase';

// Simple user type - just what Firebase Auth provides
interface SimpleUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthState {
  user: FirebaseUser | null;
  simpleUser: SimpleUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
}

interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

type AuthAction =
  | { type: 'AUTH_STATE_CHANGED'; user: FirebaseUser | null }
  | { type: 'AUTH_ERROR'; error: Error }
  | { type: 'AUTH_LOADING'; isLoading: boolean }
  | { type: 'SIGN_OUT' };

const initialState: AuthState = {
  user: null,
  simpleUser: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_STATE_CHANGED':
      return {
        ...state,
        user: action.user,
        simpleUser: action.user ? {
          uid: action.user.uid,
          email: action.user.email || '',
          displayName: action.user.displayName,
          photoURL: action.user.photoURL,
        } : null,
        isAuthenticated: !!action.user,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
    case 'AUTH_LOADING':
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case 'SIGN_OUT':
      return {
        ...initialState,
        isLoading: false,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function SimpleAuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Listen to auth state changes - NO Firestore reads!
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      dispatch({ type: 'AUTH_STATE_CHANGED', user: firebaseUser });
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      dispatch({ type: 'AUTH_LOADING', isLoading: true });
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', error: error as Error });
      throw error;
    }
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING', isLoading: true });
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', error: error as Error });
      throw error;
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_LOADING', isLoading: true });
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', error: error as Error });
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      dispatch({ type: 'SIGN_OUT' });
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', error: error as Error });
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      dispatch({ type: 'AUTH_ERROR', error: error as Error });
      throw error;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signInWithGoogle,
      signInWithEmail,
      signUp,
      signOut,
      resetPassword,
    }),
    [state, signInWithGoogle, signInWithEmail, signUp, signOut, resetPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSimpleAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
}
